/**
 * Header — Top Bar
 *
 * Shows: user name, IC role badge, organization, notifications (placeholder),
 * theme toggle, user menu.
 * Bloomberg-style: dense, functional, zero decoration.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut, Settings, User, Menu, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ROLE_LABELS } from '@/config/roles';
import { env } from '@/config/env';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';

interface HeaderProps {
  onMenuToggle: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, icRole, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initials = user
    ? (user.full_name ?? '')
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || '??'
    : '??';

  return (
    <header className="flex h-12 shrink-0 items-center justify-between border-b border-ic-border bg-ic-sidebar px-4">
      {/* Left: mobile menu + breadcrumb region */}
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuToggle}
          className="text-ic-text-tertiary hover:text-ic-text-primary lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="hidden items-center gap-2 text-xs text-ic-text-tertiary lg:flex">
          <span className="font-semibold text-ic-text-secondary">{env.appName}</span>
          <span>v{env.version}</span>
        </div>
      </div>

      {/* Right: theme toggle + notifications + user */}
      <div className="flex items-center gap-3">
        {/* Language switcher */}
        <LanguageSwitcher />

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex h-8 w-8 items-center justify-center rounded-md text-ic-text-tertiary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
          aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          title={`${theme === 'light' ? 'Dark' : 'Light'} mode`}
        >
          {theme === 'light' ? (
            <Moon className="h-4 w-4" />
          ) : (
            <Sun className="h-4 w-4" />
          )}
        </button>

        {/* Notification bell */}
        <button
          className="relative flex h-8 w-8 items-center justify-center rounded-md text-ic-text-tertiary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {/* Unread indicator */}
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-ic-danger" />
        </button>

        {/* Separator */}
        <div className="h-6 w-px bg-ic-border" />

        {/* User menu */}
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-ic-hover transition-colors"
          >
            {/* Avatar */}
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-ic-accent/20 text-ic-accent text-xs font-semibold">
              {initials}
            </div>

            <div className="hidden flex-col items-start md:flex">
              <span className="text-xs font-medium text-ic-text-primary leading-tight">
                {user?.full_name ?? 'Loading...'}
              </span>
              <span className="text-[10px] text-ic-text-tertiary leading-tight">
                {ROLE_LABELS[icRole]}
              </span>
            </div>

            <ChevronDown className="h-3.5 w-3.5 text-ic-text-tertiary" />
          </button>

          {/* Dropdown */}
          {menuOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-lg border border-ic-border bg-ic-sidebar py-1 shadow-lg">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
              >
                <User className="h-4 w-4" />
                Profile
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/settings');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
              >
                <Settings className="h-4 w-4" />
                Settings
              </button>
              <div className="my-1 h-px bg-ic-border" />
              <button
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                  navigate('/login');
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm text-ic-danger hover:bg-ic-hover transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
